import axios, { AxiosError } from 'axios';

/**
 * Client HTTP unique de l'application. Le jeton d'accès est gardé en mémoire
 * (jamais en localStorage — pour limiter l'exposition en cas de XSS) ; le jeton
 * de rafraîchissement vit dans un cookie httpOnly posé par l'API, invisible au
 * JavaScript du navigateur.
 */
let inMemoryAccessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  inMemoryAccessToken = token;
}

export function getAccessToken(): string | null {
  return inMemoryAccessToken;
}

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? 'http://localhost:4000/api/v1',
  withCredentials: true, // envoie le cookie de refresh token
  timeout: 15_000,
});

apiClient.interceptors.request.use((config) => {
  if (inMemoryAccessToken) {
    config.headers.Authorization = `Bearer ${inMemoryAccessToken}`;
  }
  return config;
});

/**
 * Rafraîchissement automatique et transparent du jeton d'accès expiré.
 * Un seul rafraîchissement à la fois (évite une tempête de requêtes /auth/refresh
 * si plusieurs appels échouent simultanément).
 */
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  refreshPromise ??= apiClient
    .post<{ accessToken: string }>('/auth/refresh')
    .then((res) => res.data.accessToken)
    .catch(() => null)
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

const REFRESH_ENDPOINT = '/auth/refresh';

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as
      (typeof error.config & { _retry?: boolean }) | undefined;

    // Un 401 sur /auth/refresh lui-même ne doit JAMAIS déclencher une nouvelle
    // tentative de rafraîchissement — sinon cet appel interne (émis par
    // `refreshAccessToken` ci-dessus) repasse par cet intercepteur, qui
    // rappelle `refreshAccessToken`, laquelle réutilise `refreshPromise`
    // (encore non résolue, puisqu'on est justement en train de la résoudre) :
    // une promesse qui s'attend elle-même, jamais résolue. Concrètement, tout
    // visiteur sans session (cas normal au premier accès, voir
    // `auth-context.tsx`) restait bloqué indéfiniment sur l'écran de
    // chargement de `RequireAuth`. Ici, on laisse simplement l'échec remonter
    // tel quel — `AuthProvider`/`refreshAccessToken` savent déjà l'interpréter.
    if (originalRequest?.url === REFRESH_ENDPOINT) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        setAccessToken(newToken);
        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      }
      setAccessToken(null);
      window.location.assign('/connexion');
    }

    return Promise.reject(error);
  },
);
